/**
 * Subscription / Access Status Composable
 * X-Subscription-Status / X-Access-Blocked 헤더 + /api/auth/me 의 access 를 감지해 전역 상태 관리.
 * 2026-07 하드 차단: accessBlocked 이면 결제화면 외 사용 불가(라우터 가드 + 팝업).
 */

import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiClient } from '@/shared/api/client'

const subscriptionStatus = ref<string>('ACTIVE')
const accessBlocked = ref<boolean>(false)
const accessReason = ref<string | null>(null)

/** /api/auth/me 의 access({blocked,reason,status})로 전역 상태 세팅(라우터 가드에서 호출). */
export function setAccess(access?: { blocked?: boolean; reason?: string | null; status?: string } | null) {
  if (!access) return
  accessBlocked.value = !!access.blocked
  accessReason.value = access.reason ?? null
  if (access.status) subscriptionStatus.value = access.status
}

// 응답 인터셉터에서 헤더/403을 읽어 전역 상태 업데이트
let interceptorRegistered = false

function registerInterceptor() {
  if (interceptorRegistered) return
  interceptorRegistered = true

  // ⚠️ 헤더를 보고 accessBlocked를 false로 되돌리지 않는다(동의 게이트와 동일한 이유) — 차단
  // 시점 직전에 이미 날아가 있던 요청이 차단 이후 뒤늦게 응답으로 도착하면, "그때는 안 막혀
  // 있었다"는 오래된 헤더값이 방금 뜬 결제 차단 팝업을 닫아버리는 경쟁 상태가 생긴다. 특히
  // 이건 결제 차단이라 그 틈에 유료 기능을 계속 쓸 수 있게 되는 문제로 이어질 수 있다.
  // true로 켜는 것만 헤더/에러로 반응하고, false로 끄는 것은 setAccess()(라우터 가드가 매
  // 네비게이션마다 /auth/me로 새로 확인한 결과)로만 하도록 제한한다.
  const instance = apiClient.getInstance()
  instance.interceptors.response.use(
    (response) => {
      const status = response.headers?.['x-subscription-status']
      if (status) subscriptionStatus.value = status
      const blocked = response.headers?.['x-access-blocked']
      if (blocked != null && String(blocked) === '1') accessBlocked.value = true
      return response
    },
    (error) => {
      const data = error?.response?.data
      if (error?.response?.status === 403 && data?.error?.code === 'SUBSCRIPTION_EXPIRED') {
        subscriptionStatus.value = data.error.subscriptionStatus || 'EXPIRED'
        if (data.error.accessBlocked) {
          accessBlocked.value = true
          accessReason.value = data.error.reason ?? accessReason.value
        }
      }
      const status = error?.response?.headers?.['x-subscription-status']
      if (status) subscriptionStatus.value = status
      const blocked = error?.response?.headers?.['x-access-blocked']
      if (blocked != null && String(blocked) === '1') accessBlocked.value = true
      return Promise.reject(error)
    }
  )
}

export function useSubscriptionStatus() {
  const { t } = useI18n()

  onMounted(() => {
    registerInterceptor()
  })

  const showBanner = computed(() => {
    return ['SUSPENDED', 'EXPIRED', 'CANCELLED'].includes(subscriptionStatus.value)
  })

  const bannerMessage = computed(() => {
    switch (subscriptionStatus.value) {
      case 'SUSPENDED':
        // 자동청구 폐지(2026-07): '결제 실패'가 아니라 관리자/수동 정지. 갱신 결제로 복구.
        return t('common.subscriptionAccess.bannerSuspended')
      case 'EXPIRED':
        return t('common.subscriptionAccess.bannerExpired')
      case 'CANCELLED':
        return t('common.subscriptionAccess.bannerCancelled')
      default:
        return ''
    }
  })

  // (isReadOnly 제거: 소비처 없음. 접근 제어는 accessBlocked 하드차단으로 일원화)

  // ── 하드 차단 팝업용 ──
  const blockedTitle = computed(() =>
    accessReason.value === 'trial_expired'
      ? t('common.subscriptionAccess.blockedTitleTrial')
      : t('common.subscriptionAccess.blockedTitleInactive'),
  )
  const blockedMessage = computed(() =>
    accessReason.value === 'trial_expired'
      ? t('common.subscriptionAccess.blockedMessageTrial')
      : t('common.subscriptionAccess.blockedMessageInactive'),
  )

  return {
    subscriptionStatus,
    showBanner,
    bannerMessage,
    accessBlocked,
    accessReason,
    blockedTitle,
    blockedMessage,
    setAccess,
  }
}
